// W01/W02 Button and IconButton (legacy BaseButton / BaseIconButton over q-btn): 40 px high and at least 120 px wide
// from 600 px (Quasar sm), 48 px high and as wide as its content under it (B1005), radius 8, label 500 and never upper-cased; disabled at legacy q-btn's opacity 0.7 (B789); outline buttons keep the border neutral and the label
// primary (the dialog "Cancel"); the auth button is the rad-medium 56 px one on the login card.
import { createContext, forwardRef, useContext, type ButtonHTMLAttributes, type ReactNode } from 'react';

type ButtonVariant = 'primary' | 'outline' | 'flat' | 'negative' | 'negative-outline' | 'auth';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly loading?: boolean;
  readonly icon?: ReactNode;
  readonly full?: boolean;
}

/**
 * Legacy's hover on an outline q-btn: Quasar's focus helper (currentColor, under its ::before black at .1 and ::after
 * white at .4) at opacity .15 over the button's fill, white in light and the 12dp surface in dark. Measured on legacy:
 * Cancel rgb(72,75,74) dark (#1643 review); the report Download and date range rgb(240,241,240) light and rgb(73,74,76)
 * dark. Shared with the report header triggers, which are outline q-btns in legacy. `enabled:` because legacy adds
 * q-hoverable only while a button can be pressed: a disabled or loading one shows no tint (#1653 review). The dark
 * triggers land a point or two off legacy's because their text, the helper's currentColor, is translucent (70 % white),
 * which color-mix and Quasar's stacked layers blend slightly differently.
 */
export const OUTLINE_HOVER = 'enabled:hover:bg-[color-mix(in_srgb,color-mix(in_srgb,color-mix(in_srgb,currentColor_90%,black)_60%,white)_15%,var(--surface-lower))] '
  + 'dark:enabled:hover:bg-[color-mix(in_srgb,color-mix(in_srgb,color-mix(in_srgb,currentColor_90%,black)_60%,white)_15%,var(--field-surface))]';

/**
 * Quasar's focus helper as a hover (visibility.sass): the element's own colour, under a black overlay at .1 and a white one
 * at .4, shown at 15 % (B995). A q-tab and a q-select's options hover with it. Tailwind builds only a class written out in
 * full, so FLAT_HOVER below repeats it after `enabled:`; ui-web test b1981-one-focus-helper keeps the two equal and every
 * other source on these exports (B1981, the #3054 review).
 */
export const QUASAR_HOVER = 'hover:bg-[color-mix(in_srgb,color-mix(in_srgb,color-mix(in_srgb,currentColor_90%,#000)_60%,#fff)_15%,transparent)]';

/**
 * Legacy's hover on a flat q-btn (an icon button, the schedule's week arrows, a flat text button): the same focus helper
 * at .15 over whatever is under the button, so it follows the glyph's colour in both themes. `hover:bg-gray-25` had no
 * dark value and drew a solid #fafafa square that hid a dark glyph (B1977). `enabled:` as OUTLINE_HOVER.
 */
export const FLAT_HOVER = 'enabled:hover:bg-[color-mix(in_srgb,color-mix(in_srgb,color-mix(in_srgb,currentColor_90%,#000)_60%,#fff)_15%,transparent)]';

const VARIANTS: Readonly<Record<ButtonVariant, string>> = {
  // Legacy's filled pp-base-button q-btn has no border, so a primary button is its padding and words wide (B812). In the
  // dark theme legacy inks every filled BaseButton `$black` (#000; BaseButton.vue `body.body--dark .pp-base-button`), not
  // the inverted text #1c1d20 (B1514).
  primary: 'bg-primary text-white dark:text-black hover:brightness-95',
  // Legacy's dark theme fills an outline q-btn with --q-dark-surface-12dp (_dark.scss .q-btn--outline:before), as a field (B617).
  // The label takes the positive ink: legacy's green (#4d833d) under legacy contrast, #3c6a2f under AA, where legacy's
  // green on its own hover tint is 4.1:1 (axe color-contrast); dark keeps its primary.
  // B1005: legacy's outline is q-btn's ::before, a 1 px line inside the button's own box, so the line is an inset ring
  // here (a border made every outline button 2 px wider than legacy's: Edit Customer 125.8 against 123.9). A caller
  // recolours it with `!inset-ring-*`.
  outline: `bg-surface dark:bg-field text-positive-ink dark:text-primary inset-ring inset-ring-line ${OUTLINE_HOVER}`,
  // Flat and filled negative q-btns have no line at all (B1005, as B812 for primary).
  flat: `bg-transparent text-primary ${FLAT_HOVER}`,
  negative: 'bg-negative text-white dark:text-black hover:brightness-95',
  'negative-outline': `bg-surface dark:bg-field text-negative inset-ring inset-ring-line ${OUTLINE_HOVER}`,
  auth: 'bg-rad-medium text-white border border-rad-medium h-14 text-[18px] mt-3 w-full',
};

/**
 * How an app sizes its Buttons. 'legacy' (the default, the back office) follows legacy's BaseButton: 48 px under 600 px,
 * 40 px with a 120 px minimum from 600 (B1005). 'fixed' keeps 40 px at every width, for the staff apps (Commander,
 * Launchpad), which have no legacy screen to match (coordinator, review of #2008). Set once at an app's root.
 */
export const ButtonSizing = createContext<'legacy' | 'fixed'>('legacy');

const SIZE = {
  // Legacy's .pp-base-button: 48 px high with min-width fit-content under 600 px, 40 px and min-width 120 from 600 (B1005).
  legacy: 'h-12 px-4 min-[600px]:h-10 min-[600px]:min-w-[120px]',
  fixed: 'h-10 px-4 sm:min-w-[120px]', // no legacy screen (the 'fixed' sizing, for Commander and Launchpad)
} as const;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', loading = false, icon, full = false, className = '', children, disabled, type = 'button', ...rest },
  ref,
) {
  const sizing = useContext(ButtonSizing);
  const size = variant === 'auth' ? '' : SIZE[sizing];
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      // Quasar's .q-btn sets line-height 1.715em, so the label sits on a 24 px line centred in the button; Next's 21 px line
      // left the words a pixel lower than legacy's (B1491).
      // Quasar's loading q-btn (B1947) is neither dimmed nor a not-allowed cursor: it is only not actionable (cursor default).
      // A Button the caller disabled stays dimmed while it loads, as Quasar dims `disable` whatever `loading` is (use-btn.js:200).
      // `data-disabled` marks the caller's own disabled state, so a caller's override (Invite's `[disabled]` 0.6) skips a loading-only Button.
      data-disabled={disabled === true ? '' : undefined}
      className={`relative inline-flex items-center justify-center gap-2 rounded-q text-[14px] leading-[1.715] font-medium tracking-[0.25px] transition-[filter,background-color] duration-200 ${loading && disabled !== true ? 'cursor-default' : 'disabled:cursor-not-allowed disabled:opacity-70'} ${size} ${VARIANTS[variant]} ${full ? 'w-full' : ''} ${className}`}
      {...rest}
    >
      {/* Quasar's q-btn__content--hidden: the label keeps its place (and the button its width) at opacity 0 while loading. */}
      <span data-testid="button-content" className={`inline-flex items-center justify-center gap-2 transition-opacity duration-300 ${loading ? 'pointer-events-none opacity-0' : ''}`}>
        {icon}
        {children}
      </span>
      {loading ? (
        // QBtn's loading slot: `absolute-full flex flex-center` around a QSpinner (Material ring, 24 px, currentColor).
        <span aria-hidden data-testid="button-spinner" className="absolute inset-0 flex items-center justify-center">
          <QSpinner />
        </span>
      ) : null}
    </button>
  );
});

/**
 * Quasar's QSpinner (the `mat` ring) as a q-btn draws it: 1.715em (`.q-btn .q-spinner`, $button-line-height: 24 px on a
 * 14 px label; measured on legacy's Activate a Device), on viewBox 25 25 50 50, a r 20 circle stroked 5 in currentColor, the svg
 * turning every 2 s and the stroke's dash drawing in and out every 1.5 s (`q-spin`, `q-mat-dash` in styles.css).
 */
function QSpinner() {
  return (
    <svg aria-hidden data-testid="q-spinner" className="size-[1.715em] animate-q-spin" viewBox="25 25 50 50">
      <circle className="animate-q-mat-dash [stroke-dasharray:1,200] [stroke-dashoffset:0]" cx="50" cy="50" r="20" fill="none" stroke="currentColor" strokeWidth="5" strokeMiterlimit="10" />
    </svg>
  );
}

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** The accessible name: icon-only buttons always carry one (WCAG 4.1.2). */
  readonly label: string;
  readonly bordered?: boolean;
  readonly tone?: 'neutral' | 'negative' | 'primary';
}

const TONES = { neutral: 'text-text-3', negative: 'text-negative', primary: 'text-primary' } as const;

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, bordered = false, tone = 'neutral', className = '', children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={`inline-flex size-9 shrink-0 items-center justify-center rounded-q transition-colors ${bordered ? 'border border-line bg-surface' : ''} ${TONES[tone]} ${FLAT_HOVER} disabled:opacity-50 ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
});

/**
 * Legacy TableActions.vue's buttons (B1248): round flat q-btns, 35 px with app.scss's 8 px radius, side by side with no gap,
 * each glyph 21 px (the table icons' q-icon at 1.5em of 14 px) in font-tertiary, which keeps its colour on hover. Hovered,
 * Quasar's focus helper lays the glyph's colour at 15 % with black at 10 % and white at 40 % over it (22 % when focused);
 * `rb-focus-helper` in styles.css is that helper. Disabled at legacy q-btn's opacity 0.7, with no tint. A row's buttons sit
 * in a plain `inline-flex` with no gap, as TableActions' div.
 */
export const TableActionButton = forwardRef<HTMLButtonElement, Omit<IconButtonProps, 'bordered' | 'tone'> & {
  /** Legacy's TableActions buttons carry a UiTooltip of their label; a bare round q-btn (POS Devices' ⋮, B1984) has none. */
  readonly tooltip?: boolean;
}>(function TableActionButton(
  { label, tooltip = true, className = '', children, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={tooltip ? label : undefined}
      className={`rb-focus-host relative inline-flex size-[35px] shrink-0 cursor-pointer items-center justify-center rounded-q text-text-3 disabled:cursor-not-allowed disabled:opacity-70 ${className}`}
      {...rest}
    >
      <span aria-hidden className="rb-focus-helper" />
      <span className="relative inline-flex [&_svg]:size-[21px]">{children}</span>
    </button>
  );
});

/**
 * Legacy TableActions.vue's labelled primary button (`:show-primary-icon="false"` with `text-underline text-primary
 * text-weight-500 ui-button-link`; B1248): a flat no-caps q-btn, 14 px at 500, underlined, padded 4 by 16 px and 36 px
 * tall, flush against the icons after it, under q-btn's focus helper, with UiTooltip's label as its title. `link` is
 * `.ui-button-link`, which turns it $darkBlue (#005ca1) when hovered: Assign Items and Make Active have it, User Access
 * (AccountUsers.vue, UsersList.vue) doesn't and keeps its primary colour. Its label sits on q-btn's 1.715em line, as
 * Button's (B1491); a 21 px line set it a pixel low (B1510).
 */
export const TableActionText = forwardRef<HTMLButtonElement, ButtonHTMLAttributes<HTMLButtonElement> & { readonly link?: boolean }>(function TableActionText(
  { className = 'text-primary', children, type = 'button', link = false, title, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      title={title ?? (typeof children === 'string' ? children : undefined)}
      className={`rb-focus-host relative inline-flex h-9 shrink-0 cursor-pointer items-center rounded-q px-4 text-[14px] leading-[1.715] font-medium whitespace-nowrap underline ${link ? 'enabled:hover:!text-[#005ca1]' : ''} disabled:cursor-not-allowed disabled:opacity-70 ${className}`}
      {...rest}
    >
      <span aria-hidden className="rb-focus-helper" />
      <span className="relative">{children}</span>
    </button>
  );
});
